import { combineReducers } from 'redux'
import moment from 'moment'
import request, { generateNotyMessage } from 'utils/request'
import {
  GROUP_TYPE_MAP,
  GROUP_LABEL_MAP,
  DATE_FORMAT_MAP,
  INSTRUMENT_TYPE_MAP,
  MODAL_CLASSES_MAP,
} from 'utils/constants'
import { getScheduleText } from '../../../../Instruments/utils/PropertyFields'
import { getStudyVersionID } from '../../../../../../../../../utils/studyConfigUtils'
import { pluralize } from 'utils/misc'
import { actions as notyActions } from 'layouts/ErrorBox'
import { DEFAULT_INSTRUMENT_ARCHITECTURE_VERSION } from '../../../../../../../../../utils/constants'
import { fetchInstruments } from '../../../../Instruments/routes/InstrumentsPage/modules/Instruments'
import { SITES_MESSAGES } from '../../../../Instruments/utils/instrumentConstants'

const { openModal, closeModal } = require('store/modal').modalActions

const SET_ALERTS = 'SET_ALERTS'
const SET_SMS_MESSAGES = 'SET_SMS_MESSAGES'
const SET_COMMUNICATION_TYPE = 'SET_COMMUNICATION_TYPE'
const SET_ALL_ANNOUNCEMENTS = 'SET_ALL_ANNOUNCEMENTS'

// ACTION CREATORS
export const setAnnouncements = ({
  announcementList,
  siteSelectionList,
  cohortList,
  isInstrument = false,
  currentVersionStatus,
  hasStudyPromotion,
}) => {
  return {
    type: SET_ALERTS,
    payload: announcementList,
    siteSelectionList,
    cohortList,
    isInstrument,
    currentVersionStatus,
    hasStudyPromotion,
  }
}
const setSMSMessages = (smsMessages, siteSelectionList, cohortList) => {
  return {
    type: SET_SMS_MESSAGES,
    payload: smsMessages,
    siteSelectionList,
    cohortList,
  }
}
export const setAllAnnouncements = announcementList => {
  return {
    type: SET_ALL_ANNOUNCEMENTS,
    announcementList,
  }
}
export const setCommunicationType = communicationType => {
  return {
    type: SET_COMMUNICATION_TYPE,
    communicationType,
  }
}

// API FUNCTIONS

const _requestAnnouncements = (studyID, forAnnouncements = true) => {
  return dispatch => {
    const url = `/control/studies/${studyID}/${forAnnouncements ? 'announcements' : 'sms'}`
    return dispatch(request({ url, success: json => Promise.resolve(json.announcements), hasLoader: true }))
  }
}

const fetchAnnouncements = (studyID, forAnnouncements = true) => {
  return (dispatch, getState) => {
    return dispatch(_requestAnnouncements(studyID, forAnnouncements)).then(communicae => {
      const { sites: { siteSelectionList = [] } = {}, participants: { cohortList = [] } = {} } = getState()
      if (forAnnouncements) {
        dispatch(setAnnouncements({ announcementList: communicae, siteSelectionList, cohortList }))
      } else {
        dispatch(setSMSMessages(communicae, siteSelectionList, cohortList))
      }
    })
  }
}

const openDeleteModal = (studyID, id, callback, success) => {
  const content = `Are you sure you want to delete the selected announcement(s)? This cannot be undone.`
  return dispatch => {
    dispatch(
      openModal({
        onConfirm: () => {
          if (callback) callback()
          dispatch(deleteAnnouncement(studyID, id, success))
          dispatch(closeModal())
        },
        onCancel: () => dispatch(closeModal()),
        content,
        className: MODAL_CLASSES_MAP.confirmation,
      }),
    )
  }
}

const deleteAnnouncement = (studyID, ids, success) => {
  const isMultiple = ids instanceof Array
  const allIds = isMultiple ? ids : [ids]
  return (dispatch, getState) => {
    const { study } = getState()
    const instrumentArchitectureVersion =
      study?.currentStudy?.config?.instrument_architecture_version || DEFAULT_INSTRUMENT_ARCHITECTURE_VERSION
    const promises = allIds.map(id => dispatch(_deleteAnnouncement(studyID, id)))
    return Promise.all(promises)
      .then(() => {
        dispatch(
          notyActions.showSuccess({
            text: generateNotyMessage(
              `Announcement${isMultiple ? 's' : ''} successfully deleted`,
              true,
              'fas fa-trash-alt',
            ),
          }),
        )
        return instrumentArchitectureVersion > DEFAULT_INSTRUMENT_ARCHITECTURE_VERSION
          ? dispatch(fetchInstruments({ studyID, isAnnouncements: true }))
          : dispatch(fetchAnnouncements(studyID))
      })
      .finally(() => {
        if (success) success()
      })
  }
}

const _deleteAnnouncement = (studyID, id) => {
  return (dispatch, getState) => {
    const { location, study } = getState()
    const studyVersionID = getStudyVersionID(location)
    const hasStudyPromotion = !!study?.currentStudy?.config?.study_promotion
    const instrumentArchitectureVersion =
      study?.currentStudy?.config?.instrument_architecture_version || DEFAULT_INSTRUMENT_ARCHITECTURE_VERSION

    const getQueryParams = () => {
      if (hasStudyPromotion) {
        return `?study_version_id=${studyVersionID}`
      }
      return ''
    }

    return dispatch(
      request({
        method: 'DELETE',
        url:
          instrumentArchitectureVersion > DEFAULT_INSTRUMENT_ARCHITECTURE_VERSION
            ? `/control/studies/${studyID}/instruments/${id}${getQueryParams()}`
            : `/control/studies/${studyID}/announcements/${id}${getQueryParams()}`,
      }),
    )
  }
}

const uploadTranslation = (studyID, instrument, file) => {
  return (dispatch, getState) => {
    const instrumentArchitectureVersion =
      getState().study?.currentStudy?.config?.instrument_architecture_version || DEFAULT_INSTRUMENT_ARCHITECTURE_VERSION
    const success = () => {
      return instrumentArchitectureVersion > DEFAULT_INSTRUMENT_ARCHITECTURE_VERSION
        ? dispatch(fetchInstruments({ studyID, isAnnouncements: true }))
        : dispatch(fetchAnnouncements(studyID))
    }
    return dispatch(
      request({
        method: 'PUT',
        url: `/control/studies/${studyID}/instruments/${instrument.id}/translations`,
        body: file,
        contentType: 'text/csv',
        hasLoader: true,
        successMessage: 'Translation successfully uploaded.',
        catchMessage: 'There was a problem uploading the file. Please try again later.',
        success,
        fail: (_response, content) => {
          const errMessage =
            content?.message || 'There was a problem uploading the file. Please try again later.'
          dispatch(notyActions.showError({ text: generateNotyMessage(errMessage, false) }))
        },
      }),
    )
  }
}

const getRecipients = ({ type, filter }, siteSelectionList, cohortList) => {
  switch (type) {
    case GROUP_TYPE_MAP.sites:
      return [
        type,
        siteSelectionList
          .map(row => ({ key: row[0].value, name: row[1].value }))
          .filter(obj => filter.site_ids.includes(obj.key)),
      ]

    case GROUP_TYPE_MAP.cohorts:
      return [
        type,
        cohortList
          .map(row => ({ key: row[0].value, name: row[1].value }))
          .filter(obj => filter.include.includes(obj.key)),
      ]

    default:
      return []
  }
}

const recipientsText = sitesNames => {
  const data = []
  Object.keys(sitesNames).forEach(name => {
    const group = GROUP_LABEL_MAP[name] || null
    if (group) {
      const item = {
        label: group,
        list: [],
      }
      sitesNames[name].forEach(site => {
        item.list.push(site.name)
      })
      data.push(item)
    }
  })
  return data
}

const _parseRecipients = (
  schedule,
  siteSelectionList,
  cohortList,
  envConfigSites = {},
  hasStudyPromotion,
  currentVersionStatus,
) => {
  if (schedule?.cohort && Array.isArray(schedule.cohort) && schedule.cohort.length) {
    const recipesNames = schedule.cohort.reduce((acc, item) => {
      const [key = '', listNames = []] = getRecipients(item, siteSelectionList, cohortList)
      if (key && listNames.length) {
        acc[key] = listNames
      }
      return acc
    }, {})
    return recipientsText(recipesNames)
  }

  if (hasStudyPromotion && schedule?.cohort?.type === 'sites') {
    if (Object.keys(envConfigSites).length && currentVersionStatus && envConfigSites[currentVersionStatus]) {
      const newScheduleCohort = {
        type: schedule?.cohort?.type,
        filter: { site_ids: [...envConfigSites[currentVersionStatus]] },
      }
      const [key = '', listNames = []] = getRecipients(newScheduleCohort, siteSelectionList, cohortList)
      if (key && listNames.length) {
        const acc = {}
        acc[key] = listNames
        return recipientsText(acc)
      }
    } else return [SITES_MESSAGES.MISSING_SITES]
  }

  if (schedule?.cohort && Object.keys(schedule.cohort).length !== 0) {
    const [key = '', listNames = []] = getRecipients(schedule.cohort, siteSelectionList, cohortList)
    if (key && listNames.length) {
      const acc = {}
      acc[key] = listNames
      return recipientsText(acc)
    }
  }
  return ['All Participants']
}

const filterAnnouncements = list =>
  Array.isArray(list) ? list.filter(item => item.type === INSTRUMENT_TYPE_MAP.announcement) : []
const _formatCommunicationList = ({
  list,
  areAnnouncements = false,
  siteSelectionList = [],
  cohortList = [],
  isInstrument = false,
  currentVersionStatus = '',
  hasStudyPromotion = false,
}) => {
  const _list = isInstrument ? filterAnnouncements(list) : list
  return _list.map(announcement => {
    const { metadata } = announcement
    const lastEdited = moment(announcement.updated)
    const schedule = isInstrument ? metadata.schedule : announcement.schedule

    let siteList = []
    let envConfigSites = {}
    if (schedule?.cohort?.type === 'sites') {
      siteList = schedule?.cohort?.filter?.site_ids
      if (!siteList.length) {
        siteList = [0]
      }
      envConfigSites = schedule?.env_config
    }

    const scheduleText = getScheduleText({ visibilityRanges: [{ start: null }], schedule })
    const recipients = _parseRecipients(
      schedule,
      siteSelectionList,
      cohortList,
      envConfigSites,
      hasStudyPromotion,
      currentVersionStatus,
    )

    const readStatus = (announcement.num_read || announcement.num_received) / announcement.num_sent || 0
    const { translations } = announcement
    const instrumentTranslations = metadata?.announcement_content?.translations
    const otherLanguages = metadata?.other_languages
    const row = [
      {
        key: 'title',
        value: announcement.title,
        sortValue: announcement.title,
        id: announcement.id,
        className: 'title-column',
        otherLanguages,
        translations: translations || instrumentTranslations,
        isAnnouncement: areAnnouncements,
      },
      { key: 'recipients', value: recipients },
      { key: 'lastEdited', value: lastEdited.format(DATE_FORMAT_MAP.main), sortValue: lastEdited.valueOf() },
      {
        key: 'scheduled',
        value: scheduleText.text,
        sortValue: scheduleText.sortValue,
        deployed: announcement.deployed !== null,
      },
    ]
    if (areAnnouncements && announcement.deployed !== null)
      row.push({
        key: 'readStatus',
        value: readStatus === Infinity ? 0 : readStatus,
        sortValue: readStatus === Infinity ? 0 : readStatus,
      })
    row.push({
      key: 'action',
      id: announcement?.id,
      type: announcement?.type,
      title: announcement?.title,
      version: announcement?.version,
      siteList,
      envConfigSites,
    })
    row.id = announcement.id
    return row
  })
}

// REDUCERS

const announcementsList = (state = [], action) => {
  switch (action.type) {
    case SET_ALERTS: {
      const { payload, siteSelectionList, cohortList, isInstrument, currentVersionStatus, hasStudyPromotion } = action
      return _formatCommunicationList({
        list: payload,
        areAnnouncements: true,
        siteSelectionList,
        cohortList,
        isInstrument,
        currentVersionStatus,
        hasStudyPromotion,
      })
    }
    default:
      return state
  }
}
const smsList = (state = [], action) => {
  switch (action.type) {
    case SET_SMS_MESSAGES: {
      const { payload, siteSelectionList, cohortList } = action
      return _formatCommunicationList({ list: payload, areAnnouncements: false, siteSelectionList, cohortList })
    }
    default:
      return state
  }
}
const allAnnouncements = (state = [], action) => {
  switch (action.type) {
    case SET_ALL_ANNOUNCEMENTS:
      return filterAnnouncements(action.announcementList)
    default:
      return state
  }
}
const currentCommunicationType = (state = 'announcements', action) => {
  switch (action.type) {
    case SET_COMMUNICATION_TYPE:
      return action.communicationType
    default:
      return state
  }
}

export const announcementsActions = {
  fetchAnnouncements,
  setCommunicationType,
  deleteAnnouncement: openDeleteModal,
  openModal,
  closeModal,
  uploadTranslation,
}

export default combineReducers({ allAnnouncements, announcementsList, smsList, currentCommunicationType })
